import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { MonitoringAiSettings } from '../../constants/monitoringAiSettings';
import { POOL_RISK_MOTION_HINTS, POOL_RISK_MOTION_HELPER_PROPS } from '../../constants/poolRiskMotion';
import {
  applyGlobalDetectionPreset,
  inferGlobalDetectionPreset,
  type GlobalDetectionPresetId,
  type DetectionLevelId,
} from '../../constants/detectionPresets';
import DetectionLevelPicker from './DetectionLevelPicker';

type Props = {
  value: MonitoringAiSettings;
  onChange: (next: MonitoringAiSettings) => void;
  onSave: (current: MonitoringAiSettings) => void | Promise<void>;
  onResetDefaults: () => void;
  saving?: boolean;
};

const numField = (
  label: string,
  helper: string,
  key: keyof MonitoringAiSettings,
  value: MonitoringAiSettings,
  onChange: (n: MonitoringAiSettings) => void,
  opts?: { min?: number; max?: number; step?: number }
) => (
  <TextField
    fullWidth
    margin="dense"
    type="number"
    label={label}
    helperText={helper}
    value={value[key] as number}
    inputProps={{
      min: opts?.min,
      max: opts?.max,
      step:
        opts?.step ??
        (key === 'jpeg_quality' || String(key).includes('ratio') || String(key).includes('confidence')
          ? 0.01
          : 1),
    }}
    onChange={(e) => {
      const v = Number(e.target.value);
      onChange({ ...value, [key]: v, detection_preset: 'custom' });
    }}
  />
);

const MonitoringAiSettingsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  onResetDefaults,
  saving = false,
}) => {
  const [preset, setPreset] = useState<GlobalDetectionPresetId>(() => inferGlobalDetectionPreset(value));

  useEffect(() => {
    setPreset(inferGlobalDetectionPreset(value));
  }, [value]);

  const setPresetAndApply = (id: DetectionLevelId) => {
    setPreset(id);
    onChange(applyGlobalDetectionPreset(id, value));
  };

  return (
    <Card>
      <CardHeader
        title="통합 관제 AI 감지"
        subheader="평소에는 「검출 성향」과 「AI 추론 해상도」만 조정."
        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestoreIcon />}
              onClick={onResetDefaults}
              disabled={saving}
            >
              기본값으로 되돌리기
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => onSave(value)}
              disabled={saving}
            >
              {saving ? '저장 중…' : '저장'}
            </Button>
          </Box>
        }
      />
      <CardContent>
        <FormControlLabel
          control={
            <Switch
              checked={value.server_ai_enabled}
              onChange={(_, checked) => onChange({ ...value, server_ai_enabled: checked })}
            />
          }
          label="중앙 AI 감시 (서버 루프)"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          켜 두면 관제센터에 로그인된 동안 서버가 CCTV 스트림에서 YOLO·위험 평가를 수행.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={value.gpu_auto_assign}
              onChange={(_, checked) => onChange({ ...value, gpu_auto_assign: checked })}
            />
          }
          label="GPU 자동 할당"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          켜면 CCTV별 GPU가 서버에서 자동 배분됨. 끄면 통합 관제 타일에서 GPU를 수동 선택할 수
          있음
        </Typography>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          AI 추론 해상도 (YOLO 입력)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          브라우저 재생(HLS) 해상도와 별개로, 서버가 RTSP 프레임을 읽은 뒤 가로를 줄여 검출.
          bbox 좌표는 원본(예: 1920×1080) 기준으로 다시 맞춤.
        </Typography>
        <FormControl fullWidth margin="dense" sx={{ mb: 2, maxWidth: 420 }}>
          <InputLabel id="capture-width-mode-label">구간·축소 방식</InputLabel>
          <Select
            labelId="capture-width-mode-label"
            label="구간·축소 방식"
            value={value.capture_width_mode}
            onChange={(e) =>
              onChange({
                ...value,
                capture_width_mode: e.target.value === 'auto' ? 'auto' : 'manual',
              })
            }
          >
            <MenuItem value="manual">
              수동 — 시설 안 CCTV 대수(1 / 2~3 / 4+)로 아래 px 적용
            </MenuItem>
            <MenuItem value="auto">
              자동 — 서버 전체 AI ON CCTV 대수로 구간 선택 + GPU·대수에 따라 px 자동 축소
            </MenuItem>
          </Select>
        </FormControl>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            {numField(
              '최대 가로(px) — CCTV 1대 구간',
              value.capture_width_mode === 'auto'
                ? '자동 모드: 이 값을 넘지 않도록 축소'
                : '해당 시설 CCTV 1대일 때 추론 가로 상한',
              'capture_max_width_1',
              value,
              onChange,
              { min: 320, max: 3840 }
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            {numField(
              '최대 가로(px) — 2~3대 구간',
              '2~3대(수동: 같은 시설 / 자동: 서버 전체)일 때 상한',
              'capture_max_width_2_3',
              value,
              onChange,
              { min: 320, max: 3840 }
            )}
          </Grid>
          <Grid item xs={12} sm={4}>
            {numField(
              '최대 가로(px) — 4대 이상 구간',
              '4대 이상일 때 상한',
              'capture_max_width_4_plus',
              value,
              onChange,
              { min: 320, max: 3840 }
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          OpenPose (포즈 추정)
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={value.openpose_enabled}
              onChange={(_, checked) => onChange({ ...value, openpose_enabled: checked })}
            />
          }
          label="ENABLED — OpenPose 사용"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          켜면 저장 직후 서버·통합 관제 AI 루프에서 OpenPose가 실행되고, 관제 화면에 스켈레톤 오버레이가
          표시됨. 모델 로드에 GPU·메모리 필요
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={value.openpose_trigger_only}
              disabled={!value.openpose_enabled}
              onChange={(_, checked) =>
                onChange({ ...value, openpose_trigger_only: checked })
              }
            />
          }
          label="TRIGGER_ONLY (기본) — 트리거 시에만 OpenPose"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          ENABLED가 켜진 경우에만 의미. 속도 급변·위험 구역 내 저속 등 조건을 만족할 때만
          OpenPose를 실행해 부하를 줄
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={value.openpose_show_skeleton}
              disabled={!value.openpose_enabled}
              onChange={(_, checked) =>
                onChange({ ...value, openpose_show_skeleton: checked })
              }
            />
          }
          label="관제 화면 — 스켈레톤 오버레이 표시"
        />
        <FormControlLabel
          control={
            <Switch
              checked={value.openpose_show_skeleton_trigger_only}
              disabled={!value.openpose_enabled || !value.openpose_show_skeleton}
              onChange={(_, checked) =>
                onChange({ ...value, openpose_show_skeleton_trigger_only: checked })
              }
            />
          }
          label="스켈레톤 — 트리거 발동 프레임에만 표시"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          켜면 OpenPose가 실제로 돌아간 프레임(openpose_ran)에만 스켈레톤을 그림. 끄면 직전
          포즈를 잠깐 유지(스티키)해 깜빡임을 줄임. 이상 자세는 bbox 라벨에도 짧게 표시됨.
        </Typography>

        <Divider sx={{ my: 2 }} />
        <DetectionLevelPicker
          variant="global"
          legend="검출 성향 (전체 기본) · 8단계"
          value={preset === 'custom' ? 'custom' : preset}
          onSelectLevel={setPresetAndApply}
          layout="radio"
        />

        <Accordion disableGutters sx={{ mt: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">고급 — 숫자로 직접 조정 (전문가)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {numField(
                  '신뢰도 하한',
                  '박스 표시·추론 최소 점수. yolov8n 물놀이장 미탐↓ 권장 약 0.30(균형). 오탐↑이면 올리고, 사람을 놓치면 내림.',
                  'min_confidence',
                  value,
                  onChange,
                  { min: 0.01, max: 1, step: 0.01 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField(
                  '최소 박스 면적 비율',
                  '작은 노이즈 제거',
                  'min_box_area_ratio',
                  value,
                  onChange,
                  { min: 0, max: 1, step: 0.0001 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField(
                  '박스 최소 변(px)',
                  '짧은 변 길이',
                  'min_box_side_px',
                  value,
                  onChange,
                  { min: 1, max: 200 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField('트래킹 IoU', '낮을수록 관대', 'track_iou_threshold', value, onChange, {
                  min: 0,
                  max: 1,
                  step: 0.01,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField(
                  '최대 미스 프레임',
                  '잠깐 안 보여도 유지',
                  'max_track_misses',
                  value,
                  onChange,
                  { min: 1, max: 100 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField(
                  '최소 연속 히트',
                  '확정 전 연속 검출 (권장 3~5)',
                  'min_track_hits',
                  value,
                  onChange,
                  { min: 1, max: 20 }
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={value.evaluate_motion_risk}
                      onChange={(e) =>
                        onChange({ ...value, evaluate_motion_risk: e.target.checked })
                      }
                    />
                  }
                  label="서버 motion·위험 평가"
                />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={POOL_RISK_MOTION_HELPER_PROPS.sx}
                >
                  {POOL_RISK_MOTION_HINTS.monitoringEvaluateMotion}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

<Accordion disableGutters sx={{ mt: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">고급 — 갱신 간격·부하</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              AI가 같은 CCTV를 다시 돌리는 주기. 카메라·PC가 많을 때만 조정.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {numField(
                  'JPEG 품질',
                  '0.1~1 (브라우저 보조 detect 시)',
                  'jpeg_quality',
                  value,
                  onChange,
                  { min: 0.1, max: 1, step: 0.05 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField('간격 기본(ms)', 'CCTV 1대', 'interval_base_ms', value, onChange, {
                  min: 100,
                  max: 10000,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField('대수당 가산(ms)', 'CCTV 추가 시', 'interval_per_cam_ms', value, onChange, {
                  min: 0,
                  max: 2000,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField('간격 상한(ms)', '최대 간격', 'interval_cap_ms', value, onChange, {
                  min: 200,
                  max: 30000,
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {numField(
                  'AI ON CCTV 합계 상한',
                  '0=무제한',
                  'max_active_detect_cams',
                  value,
                  onChange,
                  { min: 0, max: 500 }
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ mt: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              추론 tier · 알림 정책 (관제 ON 시에만 동작)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                tier A(수면·물놀이)는 매 라운드, B(데크)는 N라운드마다, C(통로·출입)는 M라운드마다
                추론. bbox는 그대로 표시하고, 아래 알림 정책은 <strong>팝업·발송용 risk_alerts</strong>
                만 걸러냄.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={value.infer_tier_enabled}
                  onChange={(_, checked) => onChange({ ...value, infer_tier_enabled: checked })}
                />
              }
              label="CCTV 용도별 infer tier 사용"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={value.infer_rotate_all_cams}
                  onChange={(_, checked) => onChange({ ...value, infer_rotate_all_cams: checked })}
                />
              }
              label="CCTV 상한 초과 시 라운드로빈 (ID 앞 N대 고정 대신)"
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                {numField(
                  'tier B(deck) 간격',
                  'N라운드마다 1회',
                  'infer_tier_b_every_n_rounds',
                  value,
                  onChange,
                  { min: 1, max: 12 }
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                {numField(
                  'tier C(통로 등) 간격',
                  'M라운드마다 1회',
                  'infer_tier_c_every_n_rounds',
                  value,
                  onChange,
                  { min: 1, max: 12 }
                )}
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={value.alert_requires_l2}
                  onChange={(_, checked) => onChange({ ...value, alert_requires_l2: checked })}
                />
              }
              label="L2(motion risk) 필수 — 끄면 위험 평가 OFF 시 알림 없음"
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="alert-min-risk-label">최소 알림 등급</InputLabel>
                  <Select
                    labelId="alert-min-risk-label"
                    label="최소 알림 등급"
                    value={value.alert_min_risk_level}
                    onChange={(e) =>
                      onChange({
                        ...value,
                        alert_min_risk_level: e.target.value === '위험' ? '위험' : '주의',
                      })
                    }
                  >
                    <MenuItem value="주의">주의 이상</MenuItem>
                    <MenuItem value="위험">위험만</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="wearable-fusion-label">웨어러블 알림 융합</InputLabel>
                  <Select
                    labelId="wearable-fusion-label"
                    label="웨어러블 알림 융합"
                    value={value.wearable_alert_fusion}
                    onChange={(e) => {
                      const v = String(e.target.value);
                      const fusion =
                        v === 'off' || v === 'gate_pool_drown' ? v : 'hint';
                      onChange({ ...value, wearable_alert_fusion: fusion });
                    }}
                  >
                    <MenuItem value="off">끔 — CCTV 알림만</MenuItem>
                    <MenuItem value="hint">힌트 — 회원 웨어러블 있으면 메타 표시</MenuItem>
                    <MenuItem value="gate_pool_drown">
                      gate — 풀 익사계는 위험·OpenPose만 (테스트용)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters sx={{ mt: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              성능 옵션 (기본: 3회/초 · imgsz 640 · FP16 켜짐 · TensorRT 끔)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                기본값(권장): 추론 <strong>초당 3회</strong> · imgsz <strong>640</strong> · FP16{' '}
                <strong>켜짐</strong> · TensorRT <strong>끔</strong>. 관제 「스트림 해상도」(480p·720p)는
                사람 눈용 재생이고, 여기 옵션은 <strong>서버 AI 추론</strong>만 변경. 더 정밀하게
                쓰려면 값을 올리거나 FP16을 끄면 됨
              </Typography>
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="infer-target-fps-label">① 목표 추론 속도 (FPS)</InputLabel>
                  <Select
                    labelId="infer-target-fps-label"
                    label="① 목표 추론 속도 (FPS)"
                    value={
                      value.infer_target_fps > 0
                        ? String(Math.round(value.infer_target_fps * 10) / 10)
                        : '0'
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      onChange({
                        ...value,
                        infer_target_fps: Number.isFinite(v) ? v : 0,
                      });
                    }}
                  >
                    <MenuItem value="3">초당 약 3회 (간격 ≥ 333ms) · 기본·권장</MenuItem>
                    <MenuItem value="5">초당 약 5회 (간격 ≥ 200ms) · 더 잦은 판정</MenuItem>
                    <MenuItem value="2">초당 약 2회 (간격 ≥ 500ms) · 부하 더 완화</MenuItem>
                    <MenuItem value="0">끔 — 「고급·갱신 간격」만 사용</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  의미: 카메라마다 YOLO를 돌리는 횟수 상한. 「3」이면 대략 1초에 3번만 판정.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  · 화면 재생은 그대로 부드럽게 나옴 (영상 FPS ≠ 추론 FPS).
                  <br />
                  · 이미 간격이 더 느리면 그대로 두고, 더 빠른 경우만 늦춤 (검출이 갑자기 빨라지지는
                  않음).
                  <br />· 「3 FPS 스킵」에 해당
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="yolo-imgsz-label">② YOLO 내부 입력 크기 (imgsz)</InputLabel>
                  <Select
                    labelId="yolo-imgsz-label"
                    label="② YOLO 내부 입력 크기 (imgsz)"
                    value={value.yolo_imgsz > 0 ? String(value.yolo_imgsz) : '0'}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      onChange({
                        ...value,
                        yolo_imgsz: Number.isFinite(v) ? v : 0,
                      });
                    }}
                  >
                    <MenuItem value="640">640 — 기본·권장 · GPU 부담↓</MenuItem>
                    <MenuItem value="960">960 — 더 정밀 · GPU 부담↑</MenuItem>
                    <MenuItem value="1280">1280 — 고정밀 · GPU 부담 큼</MenuItem>
                    <MenuItem value="0">자동 — 코드 기본(비탑뷰≈960 등)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  의미: 모델이 프레임을 정사각형에 가깝게 리사이즈해 넣는 한 변(px). 작을수록 빠르고
                  메모리를 덜 씀.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  · 위쪽 「AI 추론 해상도(가로 px)」는 캡처 축소, 여기는 모델 입력 크기 (둘 다 AI용).
                  <br />
                  · 천장 탑뷰 고해상도 경로에는 적용하지 않아 기존 인식률 유지.
                  <br />· 「640 imgsz」에 해당 (선택 시에만).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  sx={{ mt: 0.5, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={value.yolo_fp16}
                      onChange={(_, checked) => onChange({ ...value, yolo_fp16: checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">③ FP16 추론 (NVIDIA GPU) · 기본 켜짐</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        half 정밀도 · 점수 미세 차이 시 끄기
                      </Typography>
                    </Box>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  의미: GPU에서 숫자를 반정밀도(16비트)로 계산해 보통 더 빠름. CPU만 쓰는 환경에서는
                  효과가 거의 없음
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  · 기본 켜짐(속도↑). 박스·신뢰도가 이상하면 끔. CPU만 쓰면 효과가 거의 없음.
                  <br />· TensorRT(.engine) 사용 중에는 FP16 스위치 미적용(엔진 자체 정밀도).
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  sx={{ mt: 0.5, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      checked={Boolean(value.yolo_tensorrt)}
                      onChange={(_, checked) =>
                        onChange({ ...value, yolo_tensorrt: checked })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">④ TensorRT (.engine) · 기본 꺼짐</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        고급 · 엔진 파일 필요 · 없으면 .pt 폴백
                      </Typography>
                    </Box>
                  }
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  의미: 선택한 YOLO 모델과 같은 이름의 <code>.engine</code>이{' '}
                  <code>backend/models/</code>에 있으면 Ultralytics TensorRT로 추론. 없으면
                  경고 후 기존 <code>.pt</code>로 동작.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  · 예: <code>yolov8s.pt</code> → <code>yolov8s.engine</code> (또는{' '}
                  <code>_fp16</code>/<code>_tensorrt</code> 접미).
                  <br />· INT8은 캘리브레이션이 필요해 이 스위치에 미포함 (FP16 엔진 권장).
                  <br />· 저장 후 모델 캐시가 갱신됨. 엔진 로드 실패 시에도.pt로 폴백.
                </Typography>
              </Grid>
            </Grid>
            <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" component="div">
                기본이 이미 권장치(3·640·FP16, TensorRT 끔). 더 필요할 때만 Nano(
                <code>yolov8n.pt</code>)를 「YOLO 검출 모델」에서 선택하거나, 관제 스트림을
                480p~720p로 설정. 저장해야 서버에 반영됨.
              </Typography>
            </Alert>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  onChange({
                    ...value,
                    infer_target_fps: 3,
                    yolo_imgsz: 640,
                    yolo_fp16: true,
                    yolo_tensorrt: false,
                  })
                }
              >
                권장값으로 (3 · 640 · FP16 켜기 · TensorRT 끔)
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  onChange({
                    ...value,
                    infer_target_fps: 0,
                    yolo_imgsz: 0,
                    yolo_fp16: false,
                    yolo_tensorrt: false,
                  })
                }
              >
                전부 끔/자동 (구 동작에 가깝게)
              </Button>
            </Box>
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                브라우저 팁(코드 변경 없음): Chrome에서 하드웨어 가속이 꺼져 있으면 타일 많은 관제에서
                CPU만 사용. 설정 → 시스템 → 「그래픽 가속 사용」을 켠 뒤 재시작.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default MonitoringAiSettingsCard;
